class HeaderComponent extends HTMLElement {
  #initialHeaderTop = 0;
  #scrollHandler = null;
  #resizeObserver = null;

  constructor() {
    super();

    this.menuToggles = [];
    this.menuClose = null;
    this.mobileMenuOverlay = null;
    this.mobileMenu = null;
    this.mobileShowcaseNavSource = null;
    this.mobileShowcaseNavTarget = null;
  }

  connectedCallback() {
    this.menuToggles = [...this.querySelectorAll('[data-ref="menu-toggle"]')];
    this.menuClose = this.querySelector('[data-ref="menu-close"]');
    this.mobileMenuOverlay = this.querySelector('[data-ref="mobile-menu-overlay"]');
    this.mobileMenu = this.querySelector('[data-ref="mobile-menu"]');
    this.mobileShowcaseNavSource = this.querySelector('[data-ref="mobile-showcase-nav-source"]');
    this.mobileShowcaseNavTarget = this.querySelector('[data-ref="mobile-showcase-nav-target"]');

    this.#syncMobileShowcaseNav();

    this.menuToggles.forEach(toggle => {
      toggle.addEventListener('click', this.#handleMenuOpen);
    });

    if (this.menuClose) {
      this.menuClose.addEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.addEventListener('click', this.#handleOverlayClick);
    }

    document.addEventListener('keydown', this.#handleKeyDown);

    // Initialiser l'observation sticky si le header est sticky
    this.#initStickyObserver();

    this.updateHeaderOffsetHeight();
    this.#resizeObserver = new ResizeObserver(() => this.updateHeaderOffsetHeight());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.menuToggles.length) {
      this.menuToggles.forEach(toggle => {
        toggle.removeEventListener('click', this.#handleMenuOpen);
      });
    }

    if (this.menuClose) {
      this.menuClose.removeEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.removeEventListener('click', this.#handleOverlayClick);
    }

    document.removeEventListener('keydown', this.#handleKeyDown);

    // Nettoyer l'observer
    this.#cleanupStickyObserver();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  #handleMenuOpen = () => {
    this.openMobileMenu();
  };

  #handleMenuClose = () => {
    this.closeMobileMenu();
  };

  #handleOverlayClick = (event) => {
    // Fermer le menu si on clique sur l'overlay (pas sur le menu lui-même)
    if (event.target === this.mobileMenuOverlay) {
      this.closeMobileMenu();
    }
  };

  #handleKeyDown = (event) => {
    // Fermer le menu avec la touche Escape
    if (event.key === 'Escape' && this.mobileMenuOverlay.classList.contains('is-open')) {
      this.closeMobileMenu();
    }
  };

  #syncMobileShowcaseNav() {
    if (!this.mobileShowcaseNavTarget) {
      return;
    }

    // Parse the Liquid-rendered showcase entries (those that contain product tabs)
    // so we can keep their richer markup while still emitting an entry for every
    // megamenu — including the "plain" ones with no product tabs.
    const templateEntries = new Map();
    const sourceMarkup = this.mobileShowcaseNavSource?.innerHTML?.trim();
    if (sourceMarkup) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sourceMarkup;
      tmp.querySelectorAll('details.mmenu__item--showcase').forEach((entry) => {
        const title = entry.querySelector('.mmenu__link-text')?.textContent?.trim();
        if (title) {
          templateEntries.set(title, entry.outerHTML);
        }
      });
    }

    // Collect every kind of megamenu container that lives in the desktop
    // header so we can build mobile entries for all of them — not just the
    // dropdown-based ones. The "products mega menu" block uses a `.pmm`
    // wrapper instead of <dropdown-component>.
    const desktopMegamenus = [
      ...this.querySelectorAll('dropdown-component[data-hover-menu], .pmm'),
    ];

    // No desktop megamenus rendered (e.g. small viewport SSR) — fall back to
    // whatever the template gave us (which may be empty).
    if (desktopMegamenus.length === 0) {
      this.mobileShowcaseNavTarget.innerHTML = sourceMarkup || '';
      return;
    }

    const html = desktopMegamenus.map((item) => {
      // Title lookup works for both megamenu types (they share the
      // `.header__menu-link-text` class on their trigger).
      const title = item.querySelector('.header__menu-link-text')?.textContent?.trim();
      if (!title) return '';

      // Prefer the Liquid-rendered entry (showcase tabs etc.) when available.
      if (templateEntries.has(title)) {
        return templateEntries.get(title);
      }

      // Products mega menu: build an editorial mobile experience — a
      // horizontal-snap card carousel with images, index numbers and
      // categories, plus the optional promo banner. Falls back to a plain
      // link list if no product images exist.
      if (item.classList.contains('pmm')) {
        return this.#buildMobilePmmEntry(item, title);
      }

      // Regular dropdown megamenu: clone the desktop dropdown content.
      const panel = item._megamenuContent || item.querySelector('[data-ref="dropdown-content"]');
      const content = panel?.querySelector('.header__submenu--megamenu__content');
      if (!content || !content.innerHTML.trim()) {
        return '';
      }

      return `
        <details class="mmenu__item mmenu__item--showcase">
          <summary class="mmenu__link mmenu__link--top">
            <span class="mmenu__link-text">${this.#escapeHtml(title)}</span>
            <span class="mmenu__chevron" aria-hidden="true"></span>
          </summary>
          <div class="mmenu__panel mmenu__panel--showcase mmenu__panel--desktop-clone">
            ${content.innerHTML}
          </div>
        </details>
      `;
    }).join('');

    this.mobileShowcaseNavTarget.innerHTML = html;
  }

  #buildMobilePmmEntry(pmm, title) {
    const rows = [...pmm.querySelectorAll('.pmm__row')];
    if (rows.length === 0) return '';

    // Editorial vertical "index" — each product is a full-width row:
    //   [ big index ]  product name           [ thumb ]
    //                  CATEGORY · small caps
    // Hairline divider between rows. Tap whole row to navigate.
    const items = rows.map((row, idx) => {
      const slot = row.dataset.pmmRow;
      const href = row.getAttribute('href') || '#';
      const name = row.querySelector('.pmm__row-name')?.textContent?.trim() || '';
      const cat  = row.querySelector('.pmm__row-cat')?.textContent?.trim() || '';
      const indexLabel = row.querySelector('.pmm__row-index')?.textContent?.trim()
        || String(idx + 1).padStart(2, '0');

      const slide = pmm.querySelector(`.pmm__slide[data-slide="${slot}"] img`);
      const imgSrc = slide?.getAttribute('src') || '';
      const imgAlt = slide?.getAttribute('alt') || name;

      const thumb = imgSrc
        ? `<span class="pmm-m__thumb" style="background-image: url('${this.#escapeAttr(imgSrc)}');" role="img" aria-label="${this.#escapeAttr(imgAlt)}"></span>`
        : `<span class="pmm-m__thumb pmm-m__thumb--empty" aria-hidden="true"></span>`;

      return `
        <a class="pmm-m__item" href="${this.#escapeAttr(href)}">
          <span class="pmm-m__num">${this.#escapeHtml(indexLabel)}</span>
          <span class="pmm-m__text">
            <span class="pmm-m__name">${this.#escapeHtml(name)}</span>
            ${cat ? `<span class="pmm-m__cat">${this.#escapeHtml(cat)}</span>` : ''}
          </span>
          ${thumb}
          <span class="pmm-m__chev" aria-hidden="true">&rarr;</span>
        </a>
      `;
    }).join('');

    // Promo banner (optional)
    const promoEl = pmm.querySelector('.pmm__promo');
    let promoHtml = '';
    if (promoEl) {
      const promoHref = promoEl.getAttribute('href') || '#';
      const eyebrow = promoEl.querySelector('.pmm__promo-eyebrow')?.textContent?.trim() || '';
      const promoTitle = promoEl.querySelector('.pmm__promo-title')?.textContent?.trim() || '';
      // Image can be inline (.pmm__promo-image background-image) or fill (on .pmm__promo)
      let promoImg = '';
      const inlineImg = promoEl.querySelector('.pmm__promo-image');
      if (inlineImg) {
        const m = (inlineImg.getAttribute('style') || '').match(/url\((['"]?)(.*?)\1\)/);
        if (m) promoImg = m[2];
      }
      if (!promoImg && promoEl.classList.contains('pmm__promo--fill')) {
        const m = (promoEl.getAttribute('style') || '').match(/url\((['"]?)(.*?)\1\)/);
        if (m) promoImg = m[2];
      }
      const promoStyle = promoImg ? `background-image: url('${this.#escapeAttr(promoImg)}');` : '';
      promoHtml = `
        <a class="pmm-m__promo${promoImg ? ' pmm-m__promo--has-image' : ''}" href="${this.#escapeAttr(promoHref)}" style="${promoStyle}">
          <div class="pmm-m__promo-content">
            ${eyebrow ? `<span class="pmm-m__promo-eyebrow">${this.#escapeHtml(eyebrow)}</span>` : ''}
            ${promoTitle ? `<p class="pmm-m__promo-title">${this.#escapeHtml(promoTitle)}</p>` : ''}
            <span class="pmm-m__promo-arrow" aria-hidden="true">&rarr;</span>
          </div>
        </a>
      `;
    }

    // Ticker (optional, decorative)
    const tickerItems = [...pmm.querySelectorAll('.pmm__ticker-item')]
      .map((n) => n.textContent.trim())
      .filter(Boolean);
    const dedupTicker = [...new Set(tickerItems)];
    const tickerHtml = dedupTicker.length
      ? `<div class="pmm-m__ticker" aria-hidden="true">
           <div class="pmm-m__ticker-track">
             ${[...dedupTicker, ...dedupTicker]
               .map((t) => `<span class="pmm-m__ticker-item">${this.#escapeHtml(t)}</span>`)
               .join('')}
           </div>
         </div>`
      : '';

    this.#injectMobilePmmStyles();

    // Forward customizer-controlled sizing variables from the desktop .pmm
    // element to the mobile entry root so admins can tweak them in Shopify.
    const pmmStyle = pmm.getAttribute('style') || '';
    const varNames = [
      '--pmm-m-num-size',
      '--pmm-m-name-size',
      '--pmm-m-cat-size',
      '--pmm-m-thumb-size',
      '--pmm-m-row-padding',
    ];
    const inlineVars = varNames
      .map((name) => {
        const re = new RegExp(name.replace(/-/g, '\\-') + '\\s*:\\s*([^;]+);?');
        const m = pmmStyle.match(re);
        return m ? `${name}: ${m[1].trim()};` : '';
      })
      .filter(Boolean)
      .join(' ');

    return `
      <details class="mmenu__item mmenu__item--showcase mmenu__item--pmm" style="${inlineVars}">
        <summary class="mmenu__link mmenu__link--top">
          <span class="mmenu__link-text">${this.#escapeHtml(title)}</span>
          <span class="mmenu__chevron" aria-hidden="true"></span>
        </summary>
        <div class="mmenu__panel mmenu__panel--pmm">
          <div class="pmm-m">
            ${tickerHtml}
            <div class="pmm-m__list">
              ${items}
            </div>
            ${promoHtml}
          </div>
        </div>
      </details>
    `;
  }

  #escapeAttr(value) {
    return String(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  #injectMobilePmmStyles() {
    if (document.getElementById('pmm-mobile-styles')) return;
    const style = document.createElement('style');
    style.id = 'pmm-mobile-styles';
    style.textContent = `
      .mmenu__panel--pmm { padding: 0 !important; }
      .pmm-m {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding: 6px 0 18px;
      }
      .pmm-m__ticker {
        position: relative;
        overflow: hidden;
        border-block: 1px solid var(--color-border);
        background: var(--color-foreground);
        color: var(--color-background);
        height: 26px;
        display: flex;
        align-items: center;
      }
      .pmm-m__ticker-track {
        display: inline-flex;
        gap: 22px;
        white-space: nowrap;
        animation: pmm-m-ticker 28s linear infinite;
        font-size: 9px;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        font-weight: 600;
        padding-left: 22px;
        width: max-content;
      }
      .pmm-m__ticker-item::after {
        content: '\\25CF';
        margin-left: 22px;
        font-size: 5px;
        vertical-align: middle;
      }
      @keyframes pmm-m-ticker {
        from { transform: translateX(0); }
        to   { transform: translateX(-50%); }
      }

      .pmm-m__list {
        display: flex;
        flex-direction: column;
        margin: 2px 18px 0;
        border-top: 1px solid var(--color-border);
      }
      .pmm-m__item {
        display: grid;
        grid-template-columns: auto 1fr var(--pmm-m-thumb-size, 48px) auto;
        column-gap: 14px;
        align-items: center;
        padding: var(--pmm-m-row-padding, 14px) 0;
        border-bottom: 1px solid var(--color-border);
        text-decoration: none;
        color: inherit;
        position: relative;
        transition: background-color 180ms ease;
      }
      .pmm-m__item:active {
        background-color: var(--color-input-border-low-opacity, rgba(0,0,0,0.04));
      }
      .pmm-m__num {
        font-family: var(--font-h2--family, var(--font-h3--family, var(--font-body--family)));
        font-size: var(--pmm-m-num-size, 24px);
        line-height: 1;
        letter-spacing: -0.02em;
        font-weight: 400;
        font-feature-settings: "tnum" 1, "lnum" 1;
        opacity: 0.85;
        min-width: 38px;
      }
      .pmm-m__text {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
      }
      .pmm-m__name {
        font-family: var(--font-h3--family, var(--font-body--family));
        font-size: var(--pmm-m-name-size, 15px);
        line-height: 1.2;
        font-weight: 500;
        letter-spacing: -0.005em;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .pmm-m__cat {
        font-size: var(--pmm-m-cat-size, 9px);
        letter-spacing: 0.22em;
        text-transform: uppercase;
        opacity: 0.55;
        font-weight: 600;
      }
      .pmm-m__thumb {
        display: block;
        width: var(--pmm-m-thumb-size, 48px);
        height: var(--pmm-m-thumb-size, 48px);
        border-radius: 8px;
        background-color: var(--color-input-border-low-opacity, rgba(0,0,0,0.05));
        background-size: cover;
        background-position: center;
        flex-shrink: 0;
      }
      .pmm-m__thumb--empty {
        background-image:
          linear-gradient(135deg, transparent 49%, currentColor 49%, currentColor 51%, transparent 51%);
        opacity: 0.15;
      }
      .pmm-m__chev {
        font-size: 14px;
        line-height: 1;
        opacity: 0.5;
        transition: transform 220ms ease, opacity 220ms ease;
        padding-left: 4px;
      }
      .pmm-m__item:hover .pmm-m__chev,
      .pmm-m__item:active .pmm-m__chev {
        opacity: 1;
        transform: translateX(4px);
      }
      @media (max-width: 360px) {
        .pmm-m__item { column-gap: 10px; }
      }

      .pmm-m__promo {
        margin: 4px 18px 0;
        display: flex;
        align-items: stretch;
        text-decoration: none;
        color: inherit;
        background-color: var(--color-foreground);
        color: var(--color-background);
        border-radius: 14px;
        overflow: hidden;
        position: relative;
        min-height: 110px;
      }
      .pmm-m__promo--has-image {
        background-size: cover;
        background-position: center;
        color: #fff;
      }
      .pmm-m__promo--has-image::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 70%);
      }
      .pmm-m__promo-content {
        position: relative;
        z-index: 1;
        padding: 18px 20px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 6px;
        flex: 1 1 auto;
      }
      .pmm-m__promo-eyebrow {
        font-size: 9px;
        letter-spacing: 0.24em;
        text-transform: uppercase;
        font-weight: 700;
        opacity: 0.85;
      }
      .pmm-m__promo-title {
        margin: 0;
        font-size: 14px;
        line-height: 1.3;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        font-weight: 600;
      }
      .pmm-m__promo-arrow {
        align-self: flex-end;
        font-size: 18px;
        line-height: 1;
        margin-top: 4px;
      }

      /* Reveal animation when the <details> is opened */
      .mmenu__item--pmm[open] .pmm-m__item {
        animation: pmm-m-row-in 320ms ease both;
      }
      .mmenu__item--pmm[open] .pmm-m__item:nth-child(1) { animation-delay: 30ms; }
      .mmenu__item--pmm[open] .pmm-m__item:nth-child(2) { animation-delay: 60ms; }
      .mmenu__item--pmm[open] .pmm-m__item:nth-child(3) { animation-delay: 90ms; }
      .mmenu__item--pmm[open] .pmm-m__item:nth-child(4) { animation-delay: 120ms; }
      .mmenu__item--pmm[open] .pmm-m__item:nth-child(5) { animation-delay: 150ms; }
      .mmenu__item--pmm[open] .pmm-m__item:nth-child(6) { animation-delay: 180ms; }
      .mmenu__item--pmm[open] .pmm-m__item:nth-child(n+7) { animation-delay: 210ms; }
      @keyframes pmm-m-row-in {
        from { opacity: 0; transform: translateX(8px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @media (prefers-reduced-motion: reduce) {
        .pmm-m__ticker-track,
        .mmenu__item--pmm[open] .pmm-m__item { animation: none !important; }
      }
    `;
    document.head.appendChild(style);
  }

  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  openMobileMenu() {
    this.#syncMobileShowcaseNav();

    // Nettoyer les classes pour éviter les conflits
    this.mobileMenuOverlay.classList.remove('is-closing');
    this.mobileMenuOverlay.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    // Focus sur le bouton de fermeture pour l'accessibilité
    setTimeout(() => {
      this.menuClose?.focus();
    }, 150);
  }

  closeMobileMenu() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.mobileMenuOverlay.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer l'overlay
    setTimeout(() => {
      this.mobileMenuOverlay.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');

      // Remettre le focus sur o botão menu visível
      const visibleToggle = this.menuToggles.find(t => t.offsetParent !== null);
      visibleToggle?.focus();
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  /**
   * Met à jour la hauteur de l'offset du header, utile pour la position top des megamenus.
   */
  updateHeaderOffsetHeight = () => {
    this.style.setProperty('--header-offset-height', `${this.offsetHeight}px`);
  };

  /**
   * Initialise l'observation du header pour détecter quand il devient sticky
   */
  #initStickyObserver() {
    // Vérifier si le header est configuré comme sticky
    const headerGroup = this.closest('.shopify-section-group-header-group');
    if (!headerGroup || getComputedStyle(headerGroup).position !== 'sticky') {
      return;
    }

    // Calculer la position initiale réelle du header
    document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
      if (section === headerGroup) {
        return;
      }
      this.#initialHeaderTop += section.offsetHeight;
    });

    // Créer le gestionnaire de scroll avec throttling
    this.#scrollHandler = this.#throttle(() => {
      this.#checkStickyState();
    }, 10);

    // Ajouter l'écouteur de scroll
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });

    // Vérifier l'état initial
    this.#checkStickyState();
  }

  /**
   * Vérifie l'état sticky du header
   */
  #checkStickyState() {
    if (this.#initialHeaderTop === null) return;

    let offset = this.#initialHeaderTop;
    if (offset === 0) offset = 20;

    // Le header est sticky quand le scroll dépasse sa position initiale
    const isSticky = window.scrollY >= offset;

    this.dataset.stickyState = isSticky ? 'active' : 'inactive';
  }

  /**
   * Throttle function pour optimiser les performances
   */
  #throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * Nettoie l'observer lors de la destruction du composant
   */
  #cleanupStickyObserver() {
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
    this.#initialHeaderTop = null;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
